/**
 * O "Excluir" que o legado REEXIBE para "Apenas Consulta" na edição de um compromisso, e a
 * confirmação que ele abre só com "Cancelar".
 *
 * DEFEITO DO LEGADO REPRODUZIDO, por decisão do fundador (defeito do legado se porta igual, num
 * ponto só), e ele mora INTEIRO neste arquivo, ligado pela constante abaixo:
 *
 *   1. A trava do papel esconde todo botão no login e, depois, só reage a nó ADICIONADO ao
 *      documento (`admin.html:3403-3419`, `observe(... { childList: true, subtree: true })`).
 *   2. Ao abrir a edição, `abrirModalCompromissoEdicao` faz `btn-excluir-compromisso.style.display
 *      = ''` (`admin.html:4593`): mudar o estilo não adiciona nó, a trava não vê, e o "Excluir"
 *      volta a aparecer para consulta. "Salvar", "Cancelar" e o × continuam escondidos.
 *   3. Clicado, `mostrarConfirmacao` faz `btnCancelar.style.display = ''` (`admin.html:7669`), e a
 *      confirmação "Excluir este compromisso?" aparece com o "Cancelar" e SEM o "OK" nem o ×, que a
 *      trava escondeu no login. Consulta nunca consegue confirmar, então nada é apagado.
 *
 * O `ModalLegado` comum tira o rodapé inteiro de consulta (achado 5), por isso o "Excluir" e o
 * "Cancelar" daqui vão no CORPO do modal, com o mesmo desenho do rodapé do `Modal` do Chassi
 * (`packages/ui/src/components/Modal.tsx`: `flex justify-end gap-cx-2 border-t`, e as margens
 * negativas devolvem o respiro que o corpo tem e o rodapé não).
 *
 * Corrigir o legado aqui é trocar a constante para `false`: consulta volta a não ver o "Excluir".
 */

// O que o modal recebe como conteúdo.
import type { ReactNode } from 'react';
// O botão do Chassi: o produto não desenha botão.
import { Botao } from '@complexity/ui';
// O dicionário: a frase em português é a chave.
import { t } from '@complexity/i18n';
// O modal do legado, peça comum: ×, ESC, clique fora e o achado 5 num ponto só.
import { ModalLegado } from '../../comum/modal-legado.js';

/** `true` reproduz o defeito do legado; `false` o corrige. É a única linha a mexer. */
export const LEGADO_REEXIBE_EXCLUIR_PARA_CONSULTA = true;

/**
 * O "Excluir" VERMELHO, pela decisão do fundador de 19/09/2026: toda ação de apagar, excluir ou
 * remover é vermelha em todo lugar, como o aviso de perigo do legado (`.btn-perigo{ background:
 * var(--erro) }`, `admin.html:1519`). No legado este botão é o secundário neutro (`admin.html:2898`),
 * e a decisão troca isso de propósito.
 *
 * POR QUE O `!`: o Chassi não tem variante de perigo, e o `Botao` concatena a classe da variante
 * com esta sem juntar as que disputam a mesma propriedade; sem o `!`, a cor da variante vence pela
 * ordem do CSS (achado 2 da verificação da A2, o mesmo motivo do Excluir de `telas/clientes`). O
 * texto é `marca-contraste`, o `--acao-texto` que o legado põe sobre o `--erro` nos dois temas. O
 * `!` vale também no hover, então o botão continua vermelho sob o ponteiro.
 */
export const CLASSE_DE_EXCLUIR = '!border-erro !bg-erro !text-marca-contraste';

/** O rodapé desenhado dentro do corpo, para consulta, que perde o rodapé de verdade (achado 5). */
function RodapeNoCorpo({ children }: { readonly children: ReactNode }) {
  return <div className="-mx-cx-5 -mb-cx-4 mt-cx-4 flex justify-end gap-cx-2 border-t border-borda px-cx-5 py-cx-4">{children}</div>;
}

/** O "Excluir" reexibido na edição de consulta, sozinho no rodapé, como no legado. */
export function ExcluirDeConsulta({ aoExcluir }: { readonly aoExcluir: () => void }) {
  return (
    <RodapeNoCorpo>
      <Botao variante="secundario" className={CLASSE_DE_EXCLUIR} onClick={aoExcluir}>
        {t('Excluir')}
      </Botao>
    </RodapeNoCorpo>
  );
}

/**
 * A confirmação de consulta: título "Confirmar", a frase, e só o "Cancelar". Não existe caminho que
 * responda "sim": "Cancelar", ESC e o clique fora só fecham, como `onCancelar`, `onKeydown` e
 * `onOverlayClick` do legado (`admin.html:7684-7686`).
 */
export function ConfirmacaoSoComCancelar({
  aberta,
  mensagem,
  aoCancelar,
}: {
  readonly aberta: boolean;
  readonly mensagem: string;
  readonly aoCancelar: () => void;
}) {
  return (
    <ModalLegado aberto={aberta} aoFechar={aoCancelar} titulo={t('Confirmar')} testid="modal-confirmacao">
      {/* O mesmo parágrafo da confirmação comum (`comum/confirmacao.tsx`). */}
      <p className="whitespace-pre-line text-texto-suave" data-testid="confirmacao-mensagem">
        {mensagem}
      </p>
      <RodapeNoCorpo>
        <Botao variante="secundario" onClick={aoCancelar}>
          {t('Cancelar')}
        </Botao>
      </RodapeNoCorpo>
    </ModalLegado>
  );
}
